/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-logo.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { GetMimeType } from './image-service';
import { useTranslation } from 'hooks';
import NotificationService from 'utils/notification';
import './styles.scss';

const imageMaxSize = 1048576; // 1mb
const imageMaxWidth = 500; // px
const imageMaxHeight = 500; // px
const acceptedFileTypes: Accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
};

interface EditLogoInterface {
    logo: string;
    onLogoChange(base64Src: string | ArrayBuffer): any;
    disabled?: boolean;
}

const EditLogo = ({ logo, onLogoChange, disabled }: EditLogoInterface) => {
    const { t } = useTranslation();
    const [imgSrc, setImgSrc] = useState(null);
    const [imgError, setImgError] = useState('');

    useEffect(() => {
        if (logo) {
            const mimeType = GetMimeType(logo);
            if (mimeType) setImgSrc(`data:${mimeType};base64,${logo}`);
            else setImgSrc(logo);
        } else setImgSrc(logo);
    }, [logo]);

    const verifyFile = (files: File[]) =>
        new Promise((res) => {
            if (files?.length > 0) {
                const currentFile = files[0];
                const image = new Image();
                image.addEventListener('load', () => {
                    const currentFileType = currentFile.type;
                    const currentFileSize = currentFile.size;
                    if (currentFileSize > imageMaxSize) {
                        setImgError(t('admin:imageFileTooLarge'));
                        res(false);
                    } else if (!Object.keys(acceptedFileTypes).includes(currentFileType)) {
                        setImgError(t('admin:thisFileIsNotAllowed'));
                        return res(false);
                    } else if (image.width > imageMaxWidth || image.height > imageMaxHeight) {
                        // eslint-disable-next-line max-len
                        setImgError(t('admin:logoShouldBeMaximumOf'));
                        return res(false);
                    } else {
                        setImgError('');
                        res(true);
                    }
                });
                image.addEventListener('error', (e: any) => {
                    res(false);
                });
                image.src = URL.createObjectURL(currentFile);
            }
        });

    const onDrop = async (acceptedFiles: File[]) => {
        if (logo) NotificationService.showNotification(t('admin:logoUploadedAlready'));
        else if (acceptedFiles?.length > 0) {
            const isVerified = await verifyFile(acceptedFiles);
            if (isVerified) {
                // image base64
                const currentFile = acceptedFiles[0];
                const reader = new FileReader();
                reader.addEventListener(
                    'load',
                    () => {
                        onLogoChange(reader.result);
                    },
                    false,
                );

                reader.readAsDataURL(currentFile);
            }
        } else {
            // They dropped an invalid file type
            setImgError(t('admin:thisFileIsNotAllowed'));
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: acceptedFileTypes });

    return (
        <>
            <div className={`logo-wrapper ${imgError ? 'error' : ''}`}>
                <div className="label">{`${t('admin:logo')}`}</div>
                <div
                    className="drag-and-drop-area"
                    {...getRootProps()}
                    style={disabled ? { pointerEvents: 'none' } : {}}
                    data-testid="drag-and-drop-area"
                >
                    <input {...getInputProps()} />
                    <FontAwesomeIcon icon={['fal', 'cloud-upload']} className="upload-icon icon" />
                    {isDragActive ? (
                        <p>{`${t('admin:dropTheFilesHere')}`}</p>
                    ) : (
                        <p>{`${t('admin:dragOrClickToUpload')}`}</p>
                    )}
                </div>
                <div className="image-area">
                    <button
                        disabled={!imgSrc || disabled}
                        className={`${!imgSrc || disabled ? 'disabled' : ''} delete-btn`}
                        type="button"
                        onClick={() => {
                            onLogoChange(null);
                        }}
                        data-testid="delete-button"
                    >
                        <FontAwesomeIcon icon={['fal', 'trash']} className="upload-icon icon" />
                        {`${t('delete')}`}
                    </button>
                    {imgSrc ? (
                        <img className="preview-image" src={imgSrc} alt="" />
                    ) : (
                        <p>{`${t('admin:noImageSelected')}`}</p>
                    )}
                </div>
            </div>
            {imgError ? (
                <span id="imgSrc-help-text-error" className="remaining-text red">
                    {imgError}
                </span>
            ) : (
                <span id="imgSrc-help-text" className="remaining-text">{`${t(
                    'admin:MaximumFileSizeIsLogoShouldBeMaximum',
                    { mbSize: 1, widthSize: imageMaxWidth, heightSize: imageMaxHeight },
                )}`}</span>
            )}
        </>
    );
};

export default EditLogo;
